/**
 * The shapes this console CONSUMES, transcribed from `docs/plan/contracts/openapi.yaml`.
 *
 * Nothing here is invented. Every field below appears in a schema in the published contract
 * or in a published fixture, and `tests/contract/screens.test.ts` asserts that against the
 * fixture files rather than against this file, so a divergence is a red test rather than a
 * comment nobody re-reads (README 4.2).
 *
 * What is deliberately ABSENT: any row value. CONSOLES.md 6 - the Fleet Console never reads
 * customer data, not aggregated, not sampled, not "just counts for a dashboard". Everything
 * typed here is a count, a state, a duration or an id, which is what the fifteen upward
 * message types carry by construction. If a screen ever needs a value that is not in this
 * file, the answer is a new message type with a schema and a review, NOT a query.
 */

// ---------------------------------------------------------------------------
// Enrollment: what a deployment IS (wf-01)
// ---------------------------------------------------------------------------

/**
 * `EnrollRequest.environmentClass`, `enum: [dev, test, smoke, stage, prod]` in
 * `docs/plan/contracts/openapi.yaml`. Also the item type of `ClientSummary.environmentClasses`.
 *
 * The same value is BOTH the deployment's environment class AND the install package's
 * `environment`, the `<env>` in `rg-<prefix>-<env>`, so there is one vocabulary rather than a
 * class here and a free-text box on the install panel. It replaced `production | uat |
 * development`; the server migrated existing rows (production to prod, uat to stage,
 * development to dev), so an old value arriving now is a server that has not migrated and is
 * refused like any other unknown class. `prod` is the default wherever one is needed.
 *
 * A runtime array rather than a bare union because `http-fleet-api.ts` has to REJECT a value
 * outside it, and a union alone erases at compile time and rejects nothing.
 *
 * THIS EXISTS BECAUSE THE CONSOLE INVENTED ITS OWN SET AND THEN TESTED AGAINST IT. Until
 * now `records.ts` typed the field `'production' | 'non-production'` and `http-fleet-api.ts`
 * threw on anything else. `non-production` is not in the contract and never was; `uat` and
 * `development` are in the contract and were refused. The mock only ever emitted
 * `production`, so every suite passed while the one legal value a real client had enrolled
 * took the whole deployments list down - the throw is inside `.map()`, so one row refuses
 * all of them. Transcribe the contract or derive it; do not restate it from memory.
 */
export const ENVIRONMENT_CLASSES = ['dev', 'test', 'smoke', 'stage', 'prod'] as const

export type EnvironmentClass = (typeof ENVIRONMENT_CLASSES)[number]

/** What an enrollment bundle is issued for when the operator does not choose. */
export const DEFAULT_ENVIRONMENT_CLASS: EnvironmentClass = 'prod'

// ---------------------------------------------------------------------------
// Upward: the phase-1 message types (wf-04). Family A only.
// ---------------------------------------------------------------------------

export type ComponentKind = 'relay' | 'ingestion' | 'lakehouse' | 'keyvault'

export interface DeployedComponent {
  kind: ComponentKind
  version: string
}

/** `DeploymentState.estate`: their Azure, which we DETECT rather than control. */
export interface Estate {
  capacitySku?: string
  region?: string
  subscriptionChanged?: boolean
  /** Bounded and pattern-constrained upstream so a denial name cannot become a free-text channel. */
  policyDenials?: string[]
}

export interface DeploymentStateMessage {
  type: 'DEPLOYMENT.STATE'
  observedAt: string
  components: DeployedComponent[]
  estate: Estate
  snapshotId?: string
  driftCount?: number
}

export type RunStatus = 'ok' | 'failed' | 'timeout'

export type ErrorClass =
  | 'timeout'
  | 'rate-limited'
  | 'auth-failed'
  | 'permission-denied'
  | 'schema-mismatch'
  | 'network'
  | 'capacity'
  | 'upstream-error'
  | 'cancelled'
  | 'unknown'

export interface RunTelemetryMessage {
  type: 'RUN.TELEMETRY'
  observedAt: string
  agentKey: string
  step: string
  durationMs: number
  status: RunStatus
  errorClass?: ErrorClass
  tokenCost?: number
  /** The model that ACTUALLY ran, not the recommended one. Parity is a measurement. */
  modelExecuted?: string
}

export interface PipelineOutcomeMessage {
  type: 'PIPELINE.OUTCOME'
  observedAt: string
  layer: 'bronze' | 'silver' | 'gold'
  ran: boolean
  stalenessHours?: number
  /** An order-of-magnitude BUCKET. Never a row count. */
  rowDeltaBucket?: string
}

/**
 * D25: an unknown denominator must never default to complete, which is why the tier is
 * carried per entity rather than assumed.
 */
export type CoverageTier = 'reconciled' | 'self-consistent' | 'unknown'

export interface CoverageEntity {
  key: string
  expectedRows?: number
  landedRows?: number
  reconciled: boolean
  coveragePct: number
  /** Stated, never null, whenever coverage is short. */
  gap?: string
  businessRelevance?: 'high' | 'medium' | 'low'
  paginationStrategy?: string
  pagesFetched?: number
  expectedHistory?: string
  coverageTier: CoverageTier
  selectionState?: 'selected' | 'decided-against' | 'no-decision'
}

/**
 * D24: `overallPct` is NEVER rendered without `worstRelevantEntity` beside it. Measured
 * against the 15 Aug state a flat rollup reads 93.5% while the critical source sat at 2%.
 */
export interface CoverageBlock {
  instanceKey: string
  overallPct: number
  reconciledAt?: string
  entities: CoverageEntity[]
  worstRelevantEntity?: { key: string; coveragePct: number; coverageTier: CoverageTier }
  currentPct?: number
  templateOrigin?: 'named' | 'bespoke'
  scopes?: { granted: string[]; missing: string[]; lastVerifiedAt?: string }
}

export interface ConnectorHealthMessage {
  type: 'CONNECTOR.HEALTH'
  observedAt: string
  instanceKey: string
  templateKey?: string
  lastSuccessAt: string
  failureCount: number
  failureClass?: ErrorClass
  p50Ms?: number
  p95Ms?: number
  coverage: CoverageBlock
}

export type UpwardMessage =
  | DeploymentStateMessage
  | RunTelemetryMessage
  | PipelineOutcomeMessage
  | ConnectorHealthMessage

/** The batch envelope a relay POSTs to `/fleet/v1/telemetry`. */
export interface TelemetryBatch {
  deploymentId: string
  batchId: string
  sequence: number
  messages: UpwardMessage[]
  sentAt: string
  bootEpoch?: string
}

// ---------------------------------------------------------------------------
// Downward: the manifest, and the ack that comes back up (wf-02)
// ---------------------------------------------------------------------------

export interface DeploymentManifest {
  deploymentId: string
  snapshotId: string
  pinnedRelayVersion: string
  minimumSchemaVersion: string
  definitionSetVersion: number
  pollIntervalSeconds: number
  pollJitterSeconds: number
  serverTime: string
  dataRegion?: string
  credentialExpiresAt?: string
  rotateAfter?: string
  entitlementSummary?: { kinds: string[]; departmentScopes: string[] }
  featureFlags?: Record<string, boolean>
}

/** Enumerated on purpose: the free-text version of this field echoes a customer payload upward. */
export type ReasonClass =
  | 'schema-invalid'
  | 'unknown-field'
  | 'unknown-kind'
  | 'hash-mismatch'
  | 'duplicate-key'
  | 'write-failed'
  | 'capacity'
  | 'permission-denied'
  | 'other'

export interface AckItem {
  kind: DefinitionKind
  key: string
  version: number
  hash: string
}

export interface AckRequest {
  deploymentId: string
  snapshotId: string
  outcome: 'applied' | 'rolled-back'
  items: AckItem[]
  /** REQUIRED when outcome is rolled-back. */
  reasonClass?: ReasonClass
  /** Field names and item indices only. Never a value read from a customer source. */
  reasonDetail?: string
  skipped?: { kind: string; key: string }[]
}

/**
 * The sixteen kinds (D5, D31). The list is READ from the schema package registry at run time
 * by `fixtures.ts` rather than trusted from here: suite E exists because a count written down
 * in one place and derived in another is a count that goes stale (README 4.5.1).
 */
export type DefinitionKind =
  | 'COMPANY'
  | 'STAKEHOLDER'
  | 'BUSINESS-CASE'
  | 'SOW-SCOPE'
  | 'USE-CASE'
  | 'ACCELERATOR'
  | 'KPI'
  | 'REPORT-DEFINITION'
  | 'PLAYBOOK'
  | 'CONNECTOR-TEMPLATE'
  | 'SOURCE-BINDING'
  | 'AGENT-DEFINITION'
  | 'MODEL-POLICY'
  | 'FLOW-DEFINITION'
  | 'ENTITLEMENT'
  | 'POLICY'

// ---------------------------------------------------------------------------
// ASK-012 · the heartbeat
// ---------------------------------------------------------------------------

/**
 * `HeartbeatRequest.critical[]`. ENUMERATED and capped at eight by the contract.
 *
 * It is a FLAG, not a report: it says "something is wrong in this class, ask me for
 * telemetry". The detail travels on `/telemetry`, which is the channel with the egress log
 * behind it. A screen that tries to render this as the failure itself is reading a doorbell
 * as a diagnosis.
 */
export type CriticalFlag =
  | 'apply-failed'
  | 'credential-expiring'
  | 'capacity'
  | 'egress-blocked'
  | 'schema-unsupported'
  | 'permission-denied'
  | 'other'
  | 'reconciliation-failed'

/** Every verb names an endpoint the relay ALREADY calls, so there is no new downward surface. */
export type HeartbeatVerb =
  | 'fetch-manifest'
  | 'fetch-definitions'
  | 'fetch-context'
  | 'fetch-library'
  | 'fetch-policy'
  | 'fetch-entitlement'
  | 'apply-release'
  | 'send-telemetry'
  | 'rotate-credential'
  | 'decommission'
  /**
   * ASK-012's eleventh verb, and the only one that does not name an endpoint the relay already
   * calls: it tells a relay to go BACK to the snapshot in `target`. Added when fleet-api built
   * the hold beneath it - the verb landed inert first, in a contract-only PR, precisely so a
   * revert could not be issued before there was a mechanism to hold a deployment at the
   * snapshot it was reverted to. Without that, the decision tree issues `fetch-library` on the
   * very next beat and walks the deployment forward again, which would look like it worked.
   */
  | 'revert-snapshot'

export interface HeartbeatInstruction {
  /** Stable across re-offers, so executing one is idempotent and a receipt can name it. */
  instructionId: string
  verb: HeartbeatVerb
  /** The plane or artifact, when the verb alone is not enough. Optional in the contract. */
  target?: string
  /**
   * Free text, and the contract is explicit that this is the ONE safe place for it: it
   * travels DOWNWARD and is written by us, so there is no customer value to leak. Do not
   * "fix" it into an enum.
   */
  reason?: string
  expiresAt: string
}

/**
 * UPWARD, therefore CLOSED (`additionalProperties: false`). Deliberately almost empty.
 *
 * Everything the fleet needs to KNOW travels on `/telemetry`. A heartbeat that grew a payload
 * would become a second telemetry path with none of that machinery, on a call made far more
 * often. Two fields here are routinely misread and the contract says so at length:
 *
 *  - `bootEpoch` is the INSTALL epoch, NOT a restart signal. It is persisted and survives a
 *    restart. Use `processStartedAt`.
 *  - `restartCount` is OPTIONAL and ABSENT IS NOT ZERO.
 */
export interface HeartbeatRequest {
  deploymentId: string
  bootEpoch: string
  sequence: number
  processStartedAt: string
  restartCount?: number
  critical?: CriticalFlag[]
}

/**
 * DOWNWARD, therefore forward compatible (D28) - `additionalProperties` is deliberately
 * absent from the schema and this interface must not be treated as exhaustive.
 */
export interface HeartbeatResponse {
  /** WE set the schedule. The relay obeys it and MAY always call early. */
  nextHeartbeatBy: string
  /**
   * MAY BE EMPTY, and an empty list is the HEALTHY STEADY STATE: "nothing to do, call back
   * by nextHeartbeatBy". The contract says in terms that it is not an error and must not be
   * rendered as one.
   */
  instructions: HeartbeatInstruction[]
}
